/*
  HALO Depoimentos: prévias dos vídeos, pop-up do vídeo completo e setas do carrossel.

  - A prévia (mp4 leve, sem som, em loop) só baixa e toca quando o card entra na tela, e
    pausa quando sai. Fica parada para quem pede menos movimento ou economia de dados.
  - Clicar num card de vídeo abre um <dialog> com o vídeo completo (Shopify, YouTube ou
    Vimeo), copiado de um <template>: nada do vídeo completo carrega antes do clique.
    Ao fechar, o player é esvaziado (o som para) e o foco volta para o card.
  - No celular, o padrão é o mosaico de duas colunas: cada card ocupa a própria altura em
    linhas de 1px (--hd-span), então o próximo cai na coluna mais curta. Depoimentos longos
    ficam cortados com um botão + que abre o texto no próprio card.
  - No formato carrossel, a lista ganha setas e barra de progresso.
*/
(function () {
  'use strict';

  // Streaming adaptativo (HLS) quando o navegador toca nativamente (iPhone, Android, Safari).
  var playsHls = document.createElement('video').canPlayType('application/vnd.apple.mpegurl') !== '';


  if (!window.customElements || customElements.get('halo-depoimentos')) return;

  var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileQuery = window.matchMedia('(max-width: 767px)');
  var saveData = !!(navigator.connection && navigator.connection.saveData);

  class HaloDepoimentos extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.scroller = this.querySelector('[data-hd-scroller]');
      if (!this.scroller) return;
      this.started = true;

      this.ac = new AbortController();
      var on = { signal: this.ac.signal };
      var passive = { signal: this.ac.signal, passive: true };
      var self = this;

      this.reduce = reduceQuery.matches;
      this.dialog = this.querySelector('[data-hd-dialog]');
      this.player = this.querySelector('[data-hd-player]');
      this.prev = this.querySelector('[data-hd-prev]');
      this.next = this.querySelector('[data-hd-next]');
      this.previews = Array.prototype.slice.call(this.querySelectorAll('video[data-src]'));
      this.allowPreview = this.dataset.previews !== 'false' && !this.reduce && !saveData;
      this.visible = new Set();
      var section = this.closest('.halo-depoimentos');
      this.masonry = !!section && section.classList.contains('halo-depoimentos--m-masonry');
      this.gridWidth = 0;

      // Prévias
      this.previews.forEach(function (video) {
        video.addEventListener('playing', function () {
          video.classList.add('is-playing');
        }, on);
      });
      if (this.allowPreview && this.previews.length) {
        this.io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) self.visible.add(entry.target);
            else self.visible.delete(entry.target);
          });
          self.syncPreviews();
        }, { rootMargin: '100px', threshold: 0.2 });
        this.previews.forEach(function (video) {
          self.io.observe(video);
        });
        document.addEventListener('visibilitychange', this.syncPreviews.bind(this), on);
      }

      // Pop-up do vídeo
      this.addEventListener('click', function (e) {
        var button = e.target.closest('[data-hd-open]');
        if (button && self.contains(button)) self.open(button);
        var more = e.target.closest('[data-hd-more]');
        if (more && self.contains(more)) self.toggleQuote(more);
      }, on);
      if (this.dialog) {
        this.dialog.querySelector('[data-hd-close]').addEventListener('click', function () {
          self.dialog.close();
        }, on);
        this.dialog.addEventListener('click', function (e) {
          if (e.target === self.dialog) self.dialog.close();
        }, on);
        this.dialog.addEventListener('close', function () {
          self.player.textContent = '';
          if (self.opener) self.opener.focus();
          self.opener = null;
          self.syncPreviews();
        }, on);
      }

      // Carrossel do celular
      this.scroller.addEventListener('scroll', function () {
        if (!self.raf) self.raf = requestAnimationFrame(self.update.bind(self));
      }, passive);
      this.ro = new ResizeObserver(function () {
        self.update();
        // O mosaico só precisa ser refeito quando a largura muda (a altura muda por causa dele).
        var width = self.scroller.clientWidth;
        if (width !== self.gridWidth) {
          self.gridWidth = width;
          self.layout();
        }
      });
      this.ro.observe(this.scroller);
      mobileQuery.addEventListener('change', this.layout.bind(this), on);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.layout.bind(this));
      if (this.prev) {
        this.prev.addEventListener('click', function () {
          self.go(-1);
        }, on);
      }
      if (this.next) {
        this.next.addEventListener('click', function () {
          self.go(1);
        }, on);
      }

      // Editor de temas: o card selecionado vem para a vista no carrossel.
      document.addEventListener('shopify:block:select', function (e) {
        if (!self.contains(e.target)) return;
        self.scrollToItem(e.target);
      }, on);

      this.update();
    }

    disconnectedCallback() {
      if (!this.started) return;
      this.started = false;
      this.ac.abort();
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    // Mosaico do celular: marca os depoimentos cortados e mede a altura de cada card.
    layout() {
      if (!this.masonry || !mobileQuery.matches) return;
      var quotes = this.scroller.querySelectorAll('[data-hd-quote]');
      quotes.forEach(function (quote) {
        var text = quote.querySelector('[data-hd-quote-text]');
        if (!text || quote.classList.contains('is-expanded')) return;
        quote.classList.toggle('is-clamped', text.scrollHeight > text.clientHeight + 1);
      });
      var gap = parseFloat(getComputedStyle(this.scroller).columnGap) || 0;
      this.scroller.querySelectorAll('[data-hd-item]').forEach(function (item) {
        var card = item.firstElementChild;
        if (!card) return;
        item.style.setProperty('--hd-span', Math.ceil(card.getBoundingClientRect().height + gap));
      });
    }

    toggleQuote(button) {
      var quote = button.closest('[data-hd-quote]');
      if (!quote) return;
      var expanded = !quote.classList.contains('is-expanded');
      quote.classList.toggle('is-expanded', expanded);
      if (expanded) quote.classList.remove('is-clamped');
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      var label = expanded ? button.dataset.labelLess : button.dataset.labelMore;
      if (label) button.setAttribute('aria-label', label);
      this.layout();
    }

    syncPreviews() {
      if (!this.allowPreview) return;
      var paused = document.hidden || (this.dialog && this.dialog.open);
      var self = this;
      this.previews.forEach(function (video) {
        if (!paused && self.visible.has(video)) {
          if (!video.getAttribute('src')) video.setAttribute('src', (playsHls && video.dataset.srcHls) || video.dataset.src);
          video.muted = true;
          var played = video.play();
          if (played && played.catch) played.catch(function () {});
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    open(button) {
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      var template = button.parentElement.querySelector('template[data-hd-video]');
      if (!template) return;
      this.player.textContent = '';
      this.player.appendChild(template.content.cloneNode(true));
      this.player.style.setProperty('--hd-ratio', button.dataset.ratio || '1.7778');
      this.dialog.setAttribute('aria-label', button.dataset.title || '');
      this.opener = button;
      this.dialog.showModal();
      this.syncPreviews();
      var video = this.player.querySelector('video');
      if (video) {
        var played = video.play();
        if (played && played.catch) played.catch(function () {});
      }
    }

    // Distância entre o início de um card e o do seguinte (largura do card + espaço).
    step() {
      var items = this.scroller.querySelectorAll('[data-hd-item]');
      if (items.length > 1) return items[1].offsetLeft - items[0].offsetLeft;
      return this.scroller.clientWidth * 0.8;
    }

    go(direction) {
      var button = direction < 0 ? this.prev : this.next;
      if (button && button.getAttribute('aria-disabled') === 'true') return;
      this.scroller.scrollBy({ left: direction * this.step(), behavior: this.reduce ? 'auto' : 'smooth' });
    }

    scrollToItem(item) {
      var first = this.scroller.querySelector('[data-hd-item]');
      if (!first || this.scroller.scrollWidth <= this.scroller.clientWidth) return;
      this.scroller.scrollTo({ left: item.offsetLeft - first.offsetLeft, behavior: this.reduce ? 'auto' : 'smooth' });
    }

    update() {
      this.raf = 0;
      var s = this.scroller;
      var max = s.scrollWidth - s.clientWidth;
      var overflow = max > 2;
      this.classList.toggle('has-overflow', overflow);
      if (!overflow) return;
      var progress = Math.min(1, Math.max(0, s.scrollLeft / max));
      var visible = Math.max(0.15, Math.min(1, s.clientWidth / s.scrollWidth));
      this.style.setProperty('--hd-thumb-w', (visible * 100).toFixed(2) + '%');
      this.style.setProperty('--hd-thumb-x', ((progress * (1 - visible)) / visible * 100).toFixed(2) + '%');
      if (this.prev) this.prev.setAttribute('aria-disabled', progress <= 0.005 ? 'true' : 'false');
      if (this.next) this.next.setAttribute('aria-disabled', progress >= 0.995 ? 'true' : 'false');
    }
  }

  customElements.define('halo-depoimentos', HaloDepoimentos);
})();
